// AddressAutocomplete.jsx  (plain JS – add types if you like)
import React, { useEffect, useRef } from "react";
import { mapsLoader } from "./googleMapsLoader";           // <- NEW
import { AddressAutoCompleteStylePatch } from "./AddressAutoCompleteStylePatch";

AddressAutoCompleteStylePatch();

/* ---------- tiny hook that keeps the Google widget in sync with React ---- */
function usePlaceAutocomplete({ value, onInput, onSelect }) {
  const containerRef = useRef(null);   // the <div> we render
  const widgetRef    = useRef(null);   // the Google element

  /* 1️⃣  Mount exactly once ------------------------------------------------ */
  // https://developers.google.com/maps/documentation/javascript/reference
  useEffect(() => {
    let widget;
    let cancelled = false;             // in case the component unmounts early

    (async () => {
      // Load Maps + the Places library once.  Key comes from .env via mapsLoader
      await mapsLoader.importLibrary("places");

      if (cancelled) return;           // component already gone

      widget = new window.google.maps.places.PlaceAutocompleteElement({
        includedRegionCodes: ["us"],
        includedPrimaryTypes: ["premise"],
      });

      const inputEl = widget.Eg;       // the <input /> inside the widget
      inputEl.placeholder = "Search postal address";

      widget.value = value ?? "";      // seed initial value *after* render
      widgetRef.current = widget;
      containerRef.current?.appendChild(widget);

      widget.addEventListener("input", e => onInput?.(e.target.value));

      widget.addEventListener("gmp-select", async ({ placePrediction }) => {
        const place = placePrediction.toPlace();
        await place.fetchFields({ fields: ["addressComponents"] });
        if (place.addressComponents) {
          onSelect?.(parseAddress(place.addressComponents));
          window.updateTextValue(inputEl, "")
        }
      });
    })();

    /* cleanup on unmount --------------------------------------------------- */
    return () => {
      cancelled = true;
      widget?.remove();
    };
  }, []);                               // ← run once only

  /* 2️⃣  push React `value` into the widget on change ----------------------- */
  useEffect(() => {
    const widget = widgetRef.current;
    if (widget && widget.value !== value) widget.value = value ?? "";
  }, [value, widgetRef]);

  return containerRef;
}

/* ---------- public component --------------------------------------------- */
export default function AddressAutocomplete(props) {
  const containerRef = usePlaceAutocomplete(props);
  return <div ref={containerRef} />;
}

/* ---------- helper: turn addressComponents into your own shape ----------- */
function parseAddress(components) {
  const addr = { address1: "", city: "", state: "", zip: "", county: "" };

  components.forEach(c => {
    const t = c.types;
    if (t.includes("street_number")) addr.address1 = `${c.longText || c.long_name} ${addr.address1}`;
    if (t.includes("route")) addr.address1 += c.longText || c.long_name;
    if (t.includes("locality")) addr.city = c.longText || c.long_name;
    if (t.includes("sublocality") && !addr.city) addr.city = c.longText || c.long_name;
    if (t.includes("administrative_area_level_1")) addr.state = c.shortText || c.short_name;
    if (t.includes("postal_code")) addr.zip = c.longText || c.long_name;
    if (t.includes("administrative_area_level_2")) addr.county = c.longText || c.long_name;
  });

  return addr;
}
